// ─── API Endpoints ──────────────────────────────────────────────────
// Typed functions for all backend endpoints (v2 with JWT auth).

import { API_BASE, API_PREFIX, apiGet, apiPost, apiPostForm, apiDelete } from './client';
import type {
  HealthResponse,
  IngestResponse,
  BatchIngestResponse,
  ImageListResponse,
  ImageListParams,
  ImageDocument,
  ImageStatusResponse,
  DeleteResponse,
  BatchDeleteResponse,
  SearchResponse,
  TextSearchRequest,
  VQAResponse,
  QueryResponse,
} from './types';

export type ImageSize = 'thumbnail' | 'medium';

// ─── Health ─────────────────────────────────────────────────────────

export function getHealth(signal?: AbortSignal) {
  return apiGet<HealthResponse>(`${API_BASE}/health`, signal);
}

export async function getHealthReady(signal?: AbortSignal) {
  return apiGet<{ status: string }>(`${API_BASE}/health/ready`, signal);
}

// ─── Ingestion ──────────────────────────────────────────────────────

export function ingestImage(file: File, signal?: AbortSignal) {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm<IngestResponse>(`${API_PREFIX}/images`, form, signal);
}

export function ingestImageByUrl(sourceUri: string, signal?: AbortSignal) {
  const form = new FormData();
  form.append('source_uri', sourceUri);
  return apiPostForm<IngestResponse>(`${API_PREFIX}/images`, form, signal);
}

export function batchIngest(files: File[], signal?: AbortSignal) {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file);
  }
  return apiPostForm<BatchIngestResponse>(
    `${API_PREFIX}/images/batch`,
    form,
    signal,
  );
}

// ─── Listing & Metadata ────────────────────────────────────────────

export function listImages(params?: ImageListParams, signal?: AbortSignal) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.status) searchParams.set('status', params.status);
  const qs = searchParams.toString();
  return apiGet<ImageListResponse>(
    `${API_PREFIX}/images${qs ? `?${qs}` : ''}`,
    signal,
  );
}

export function getImageMetadata(imageId: string, signal?: AbortSignal) {
  return apiGet<ImageDocument>(`${API_PREFIX}/images/${imageId}`, signal);
}

/**
 * Lightweight status check for polling during processing.
 * Returns ~50 bytes vs full metadata.
 */
export function getImageStatus(imageId: string, signal?: AbortSignal) {
  return apiGet<ImageStatusResponse>(
    `${API_PREFIX}/images/${imageId}/status`,
    signal,
  );
}

/**
 * Returns the direct URL for an image file.
 * Supports optional size variants: 'thumbnail' (200px), 'medium' (800px).
 */
export function getImageFileUrl(imageId: string, size?: ImageSize): string {
  const base = `${API_PREFIX}/images/${imageId}/file`;
  return size ? `${base}?size=${size}` : base;
}

// ─── Deletion ───────────────────────────────────────────────────────

export function deleteImage(imageId: string, signal?: AbortSignal) {
  return apiDelete<DeleteResponse>(`${API_PREFIX}/images/${imageId}`, signal);
}

export function batchDeleteImages(
  imageIds: string[],
  signal?: AbortSignal,
) {
  return apiPost<BatchDeleteResponse>(
    `${API_PREFIX}/images/batch-delete`,
    { image_ids: imageIds },
    signal,
  );
}

// ─── Text Search ────────────────────────────────────────────────────

export function searchText(request: TextSearchRequest, signal?: AbortSignal) {
  return apiPost<SearchResponse>(
    `${API_PREFIX}/search/text`,
    request,
    signal,
  );
}

// ─── Similar Search ─────────────────────────────────────────────────

export function searchSimilarByFile(
  file: File,
  limit = 10,
  minSimilarity?: number,
  signal?: AbortSignal,
) {
  const form = new FormData();
  form.append('file', file);
  const params = new URLSearchParams({ limit: String(limit) });
  if (minSimilarity !== undefined) {
    params.set('min_similarity', String(minSimilarity));
  }
  return apiPostForm<SearchResponse>(
    `${API_PREFIX}/search/similar?${params}`,
    form,
    signal,
  );
}

export function searchSimilarById(
  imageId: string,
  limit = 10,
  excludeSelf = true,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({
    limit: String(limit),
    exclude_self: String(excludeSelf),
  });
  return apiGet<SearchResponse>(
    `${API_PREFIX}/search/similar/${imageId}?${params}`,
    signal,
  );
}

// ─── Ask — Image Q&A ────────────────────────────────────────────────

export function askQuestion(
  imageId: string,
  question: string,
  useStoredContext = true,
  signal?: AbortSignal,
) {
  return apiPost<VQAResponse>(`${API_PREFIX}/images/${imageId}/ask`, {
    question,
    use_stored_context: useStoredContext,
  }, signal);
}

export function askWithUpload(
  file: File,
  question: string,
  signal?: AbortSignal,
) {
  const form = new FormData();
  form.append('file', file);
  form.append('question', question);
  return apiPostForm<VQAResponse>(`${API_PREFIX}/vqa`, form, signal);
}

// ─── Unified Query ──────────────────────────────────────────────────

export function unifiedQuery(
  query: string,
  imageId?: string,
  signal?: AbortSignal,
) {
  return apiPost<QueryResponse>(`${API_PREFIX}/query`, {
    query,
    image_id: imageId ?? null,
  }, signal);
}

